import { isAxiosError } from 'axios';

export function getErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return undefined;
  if ('message' in error && typeof error.message === 'string')
    return error.message;
  if ('error' in error && typeof error.error === 'string') return error.error;
  return undefined;
}

function toMessage(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (Array.isArray(value)) {
    // Terminal for arrays: falling through to Object.values(array) would
    // recurse on the same array forever when it holds no string elements.
    const messages = value.filter(
      (item): item is string => typeof item === 'string' && item.length > 0,
    );
    return messages.length > 0 ? messages.join('\n') : undefined;
  }
  if (value && typeof value === 'object') {
    return toMessage(Object.values(value));
  }
  return undefined;
}

/** HTTP status of a failed core-api call, if the error is an axios error. */
export function getAxiosErrorStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

/**
 * Extracts the human-readable detail from a core-api error response.
 * The AllExceptionsFilter puts it in `data.validationErrors` (string, array,
 * or field→message record) for validation failures, or `data.error` / `data.message`
 * otherwise; `data.error` may itself be an array of constraint messages.
 */
export function getAxiosErrorMessage(error: unknown): string | undefined {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (data && typeof data === 'object') {
      if ('validationErrors' in data) {
        const message = toMessage(data.validationErrors);
        if (message) return message;
      }
      if ('error' in data && data.error !== 'ValidationError') {
        const message = toMessage(data.error);
        if (message) return message;
      }
      if ('message' in data) {
        const message = toMessage(data.message);
        if (message) return message;
      }
    }
  }
  return getErrorMessage(error);
}

export function errorCode(error: unknown): string {
  return typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as { error: unknown }).error === 'string'
    ? (error as { error: string }).error
    : '';
}
